import {CONFIGURABLE_COLUMN_NAMES} from "../components/dashboard/columns";
import {APIClient, DASHBO_CLIENT_HEADER, DASHBO_CLIENT_HEADER_VALUE} from "./APIClient";
import {API_URL} from "../settings/local_settings";
import {LocalStorage} from "./LocalStorage";
import {toast} from "react-toastify";
import i18n from "../i18n";

// Los cuatro fetches de abajo son de FONDO: dos traen la configuración de columnas al abrir el
// Tablero y dos persisten una preferencia después de que el usuario tocó algo. Ninguno pedía un
// modal, y `alert()` es modal y NATIVO: bloquea el hilo, así que uno solo congela el render de
// todo el Tablero — el mismo modo de falla que reportó Angie (Bakián) el 2026-09-14 desde las
// filas. Van por toast: misma información, sin frenar la pantalla.
//
// ⚠️ Estos cuatro chequean `status === 'ERROR'`, que es el "colador" documentado en el CLAUDE.md
// del repo: un código tipado del backend y el `NETWORK_ERROR` sintético del `APIClient` NO
// matchean y se comen el error en silencio. No se cambia acá a propósito — es un frente aparte
// (149 call sites), y ampliar la condición en este archivo haría aparecer toasts que hoy no
// existen, sin haber medido cuáles.
function avisarErrorDeConfiguracion(responseData) {
    // Sin sesión, el error es de un pedido que quedó huérfano de un logout, no del Tablero: su
    // `error_message` es el `detail` en inglés de DRF y salía como toast en `/login` (OGGNnRpB).
    if (!LocalStorage.fetchUserToken()) return;
    toast.error(responseData['error_message'] || i18n.t('dashboard_row.error_occurred_contact_team'));
}

// Portal V2 (2026-05-28): save/delete de sets necesitan acceder al status HTTP 409 puro
// para distinguir "conflicto por assignments" del resto de errores. APIClient hoy aplana
// todo error a `{status: 'ERROR'}` perdiendo el body — para esos dos casos usamos
// fetch directo y propagamos `requires_confirmation` cuando viene del backend.
async function _fetchJson(url, method, bodyJson) {
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        [DASHBO_CLIENT_HEADER]: DASHBO_CLIENT_HEADER_VALUE,
    };
    const token = LocalStorage.fetchUserToken();
    if (token) headers['Authorization'] = 'Token ' + token;
    const conf = {method, headers};
    if (bodyJson !== undefined && bodyJson !== null) conf.body = JSON.stringify(bodyJson);
    const response = await fetch(url, conf);
    let body = null;
    try { body = await response.json(); } catch (_) { body = null; }
    return {status: response.status, body: body || {}};
}

// Lee una preferencia guardada como JSON. Un valor corrupto vuelve al default en vez de romper el
// constructor del Tablero.
function parsedPref(name, fallback) {
    const stored = LocalStorage.fetchDashboardPref(name);
    if (stored === null) return fallback;
    try {
        return JSON.parse(stored);
    } catch (e) {
        return fallback;
    }
}

export class DashboardConfig {
    constructor() {
        // Migración de las claves globales de antes (OGGNnRpB): se descartan, no se heredan.
        LocalStorage.removeLegacyDashboardPrefs();
    }

    // filters — por agencia y usuario (ver `LocalStorage.dashboardPrefKey`)

    resetFilters() {
        this.saveIssuesOnlyFilter(false);
        this.savePlatformFilter("");
        this.saveLabelsFilter([]);
        this.saveTextFilter("");
    }

    saveTextFilter(value) {
        LocalStorage.saveDashboardPref('textFilter', value);
    }

    saveIssuesOnlyFilter(value) {
        LocalStorage.saveDashboardPref('issuesOnlyFilter', JSON.stringify(value));
    }

    saveLabelsFilter(labels) {
        LocalStorage.saveDashboardPref('labelsFilter', JSON.stringify(labels));
    }

    savePlatformFilter(value) {
        LocalStorage.saveDashboardPref('platformFilter', value);
    }

    saveColumnOrdering(value) {
        const columnOrderingJson = {'columnId': value.columnId(), 'orderingDirection': value.orderingDirection()};
        LocalStorage.saveDashboardPref('columnOrdering', JSON.stringify(columnOrderingJson));
    }

    getPlatformFilter() {
        return LocalStorage.fetchDashboardPref('platformFilter') ?? '';
    }

    getTextFilter() {
        return LocalStorage.fetchDashboardPref('textFilter') ?? '';
    }

    getIssuesOnlyFilter() {
        return parsedPref('issuesOnlyFilter', false);
    }

    getLabelsFilter() {
        return parsedPref('labelsFilter', []);
    }

    getColumnOrdering() {
        return LocalStorage.fetchDashboardPref('columnOrdering');
    }

    saveColumnSet(setId, setName, columns, action = null) {
        // Si setId es null o undefined, se crea uno nuevo.
        // `action` es 'accept' o 'duplicate' cuando el caller ya resolvió el prompt de
        // impacto del Portal V2 (ColumnSetPortalImpactModal). Sin action, si el set tiene
        // portal_assignments, el backend devuelve 409 y propagamos `requires_confirmation`.
        const columnSetData = setId
            ? {id: setId, name: setName, columns: columns}
            : {name: setName, columns: columns};
        const qs = action ? `?action=${encodeURIComponent(action)}` : '';
        return _fetchJson(API_URL + 'dashboard/columns-sets' + qs, 'POST', columnSetData)
            .then(({status, body}) => {
                if (status === 409 && body && body.requires_confirmation) {
                    // El caller debe mostrar el modal de impacto y reintentar con action.
                    return Promise.resolve({
                        requires_confirmation: true,
                        assignments_count: body.assignments_count || 0,
                        assigned_clients: body.assigned_clients || [],
                    });
                }
                if (status >= 200 && status < 300) {
                    return body.data || body.object || body;
                }
                return Promise.reject({
                    status: 'ERROR',
                    errors: [{message: (body && (body.error_message || body.detail)) || 'Error al guardar el set de columnas'}],
                });
            });
    }

    deleteColumnSet(setId, action = null) {
        const qs = action ? `?action=${encodeURIComponent(action)}` : '';
        return _fetchJson(API_URL + `dashboard/columns-sets/${setId}/` + qs, 'DELETE')
            .then(({status, body}) => {
                if (status === 409 && body && body.requires_confirmation) {
                    return Promise.resolve({
                        requires_confirmation: true,
                        assignments_count: body.assignments_count || 0,
                        assigned_clients: body.assigned_clients || [],
                    });
                }
                if (status >= 200 && status < 300) {
                    return body.data || body.object || body;
                }
                return Promise.reject({
                    status: 'ERROR',
                    errors: [{message: (body && (body.error_message || body.detail)) || 'Error al borrar el set de columnas'}],
                });
            });
    }

    applyColumnSet(setId) {
        const setData = {set_id: setId};
        new APIClient(API_URL + 'dashboard/current-columns-set').call('POST', setData)
            .then((responseData) => {
                if (responseData.status === 'ERROR') {
                    avisarErrorDeConfiguracion(responseData);
                } else if (responseData.status === 'OK') {
                }
            });
    }

    // active columns

    saveActiveColumns(activeColumns) {
        this.handleDefaultColumnsChange(JSON.stringify(activeColumns))
    }

    handleColumnsSets(newColumnsSets) {
        return new APIClient(API_URL + 'dashboard/columns-sets').call('POST', newColumnsSets)
            .then((responseData) => {
                if (responseData.status === 'ERROR') {
                    return Promise.reject({
                        status: 'ERROR',
                        errors: [{ message: responseData.error_message || 'Error al guardar el set de columnas' }]
                    });
                } else if (responseData.status === 'OK' || responseData.status === 'SUCCESS') {
                    // El backend devuelve el set guardado con su ID
                    return responseData.data || responseData.object || newColumnsSets;
                }
                return null;
            })
            .catch((error) => {
                return Promise.reject({
                    status: 'ERROR',
                    errors: [{ message: error.message || 'Error al guardar el set de columnas' }]
                });
            });
    }

    handleDefaultColumnsChange(newActiveColumns) {
        new APIClient(API_URL + 'dashboard/default-columns').call('POST', newActiveColumns)
            .then((responseData) => {
                if (responseData.status === 'ERROR') {
                    avisarErrorDeConfiguracion(responseData);
                } else if (responseData.status === 'OK') {
                }
            });
    }

    retrieveColumnsSets(columnSetsCallback) {
        new APIClient(API_URL + 'dashboard/columns-sets').call('GET')
            .then((responseData) => {
                if (responseData.status === 'ERROR') {
                    avisarErrorDeConfiguracion(responseData);
                } else if (responseData.status === 'OK') {
                    let formattedResponseData = {};
                    // El backend devuelve un array de objetos con id, name y columns
                    if (Array.isArray(responseData.data.column_sets)) {
                        responseData.data.column_sets.forEach((set) => {
                            formattedResponseData[set.id] = {
                                id: set.id,
                                name: set.name,
                                columns: set.columns,
                                // Portal V2: cuántos portales de cliente tienen este set
                                // asignado. Lo usa `ColumnsSetsList` para badge + modal de
                                // impacto al guardar/borrar.
                                portal_assignments_count: set.portal_assignments_count || 0,
                                assigned_client_ids: set.assigned_client_ids || [],
                            };
                        });
                    }
                    columnSetsCallback(formattedResponseData)
                }
            });
    }

    retrieveDefaultConfiguration(activeColumnsCallback) {
        new APIClient(API_URL + 'dashboard/default-columns').call('GET')
            .then((responseData) => {
                if (responseData.status === 'ERROR') {
                    avisarErrorDeConfiguracion(responseData);
                } else if (responseData.status === 'OK') {
                    activeColumnsCallback(responseData.data)
                }
            });
    }

    static defaultActiveColumns() {
        return CONFIGURABLE_COLUMN_NAMES;
    }
}
